import { useQuery } from '@tanstack/react-query'
import { apiFetch, getCallerAgentId, hasLiveApi } from '@/api/client'
import type { AgentNode } from '@/api/map/agent'
import { isUsernameAvailable } from '@/data/api'
import { keys } from '@/api/hooks/shared/keys'

/**
 * Login identities for a hierarchy node — same rule as `agentNodeToAccount`,
 * plus display `name` so Create cannot reuse either username or display name.
 */
export function nodeLoginNames(node: Pick<AgentNode, 'name' | 'username' | 'loginEmail'>): string[] {
  const names: string[] = []
  const username = node.username?.trim().toLowerCase()
  const name = node.name.trim().toLowerCase()
  const emailLocal = node.loginEmail?.split('@')[0]?.trim().toLowerCase()
  if (username) names.push(username)
  if (name && !names.includes(name)) names.push(name)
  if (emailLocal && !names.includes(emailLocal)) names.push(emailLocal)
  return names
}

/** True when `needle` is free among the given nodes. */
export function isUsernameFreeInTree(
  needle: string,
  nodes: Pick<AgentNode, 'name' | 'username' | 'loginEmail'>[],
): boolean {
  const want = needle.trim().toLowerCase()
  if (!want) return false
  return !nodes.some((node) => nodeLoginNames(node).includes(want))
}

/**
 * Username availability for the Create wizard.
 * Live: prefer global /v1/usernames/available (mock); fall back to caller tree.
 * Demo: fixture world.
 */
async function checkUsername(username: string): Promise<boolean> {
  const needle = username.trim().toLowerCase()
  if (!needle) return false
  if (!hasLiveApi()) return isUsernameAvailable(username)

  try {
    const res = await apiFetch<{ available: boolean }>(
      `/v1/usernames/available?username=${encodeURIComponent(needle)}`,
    )
    return Boolean(res.available)
  } catch {
    // Real hierarchy-service may not expose this route yet — fall back to subtree.
    const id = getCallerAgentId()!
    const nodes = await apiFetch<AgentNode[]>(`/v1/agents/${id}/tree`)
    return isUsernameFreeInTree(needle, nodes)
  }
}

export function useUsernameAvailable(username: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.usernameAvailable(username),
    queryFn: () => checkUsername(username),
    enabled,
    staleTime: 5_000,
  })
}

/** Imperative check used by the Create page while typing. */
export function checkUsernameAvailable(username: string): Promise<boolean> {
  return checkUsername(username)
}
